"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import AngleRightBold from "@/icons/angleRightBold.svg";
import LearnMoreIcon from "@/icons/learnMore.svg";

import "swiper/css";
import "swiper/css/navigation";
import Image from "next/image";
import { getTranslation } from "@/i18n/get-translation";

interface IBlogsUpdate {
  locale: string;
}

export default function BlogsUpdate({ locale }: IBlogsUpdate) {
  const t = getTranslation(locale, "blogsUpdate");
  return (
    <section className="relative text-center mt-32 mb-52 px-20 max-md:px-7 max-md:mt-16">
      <div className="text-2xl text-secondary-800 mb-5 max-md:text-lg">
        {t("title")}
      </div>
      <h2 className="text-[3rem] max-md:text-3xl leading-[1.15] font-medium text-primary-800">
        {t("subtitle")}
      </h2>
      <a
        href="#"
        className="justify-center arrowBtn mt-2 whitespace-nowrap xl:text-xl lg:text-lg text-base text-primary-700 font-medium rounded-full py-2 leading-none flex items-center"
        aria-label="Read more blog posts and updates"
      >
        {t("learnMore")}
        <div className="relative ml-2">
          <div className="bg-primary-400 rounded-full w-10 h-10 opacity-[0.24]"></div>
          <LearnMoreIcon className="w-2.5 font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
        </div>
      </a>
      <div className="mt-8 relative">
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={12}
          slidesPerView={1.2}
          navigation={{
            nextEl: ".next-slide",
            prevEl: ".prev-slide",
          }}
          pagination={true}
          breakpoints={{
            1199: {
              slidesPerView: 4.2,
            },
            992: {
              slidesPerView: 2.2,
              pagination: false,
            },
          }}
          className="relative"
        >
          <SwiperSlide>
            <article className="text-left card-hover">
              <figure className="relative rounded-xl overflow-hidden h-56 w-full mb-7 blog-card-image">
                <div className="w-full h-full">
                  <Image
                    loading="lazy"
                    fill={true}
                    objectFit="cover"
                    src="/images/how-medical-software-is-revolutionizing-patient-care.webp"
                    alt=""
                  />
                </div>
              </figure>
              <h3 className="text-primary-800 text-2xl">
                {t("howMedicalSoftwareIsRevolutionizingPatientCare")}
              </h3>
            </article>
          </SwiperSlide>
          <SwiperSlide>
            <article className="text-left card-hover">
              <figure className="relative rounded-xl overflow-hidden h-56 w-full mb-7 blog-card-image">
                <div className="w-full h-full">
                  <Image
                    loading="lazy"
                    fill={true}
                    objectFit="cover"
                    src="/images/comprehensive-guide-to-choosing-the-right-medical-software.webp"
                    alt=""
                  />
                </div>
              </figure>
              <h3 className="text-primary-800 text-2xl">

                {t("comprehensiveGuideToChoosingTheRightMedicalSoftware")}
              </h3>
            </article>
          </SwiperSlide>
          <SwiperSlide>
            <article className="text-left card-hover">
              <figure className="relative rounded-xl overflow-hidden h-56 w-full mb-7 blog-card-image">
                <div className="w-full h-full">
                  <Image
                    loading="lazy"
                    fill={true}
                    objectFit="cover"
                    src="/images/protecting-data-with-advanced-with-cybersecurity.webp"
                    alt=""
                  />
                </div>
              </figure>
              <h3 className="text-primary-800 text-2xl">
                {t("protectingDataWithAdvancedWithCybersecurity")}
              </h3>
            </article>
          </SwiperSlide>
          <SwiperSlide>
            <article className="text-left card-hover">
              <figure className="relative rounded-xl overflow-hidden h-56 w-full mb-7 blog-card-image">
                <div className="w-full h-full">
                  <Image
                    loading="lazy"
                    fill={true}
                    objectFit="cover"
                    src="/images/simplify-medical-billing-and-administration-with-technology.webp"
                    alt=""
                  />
                </div>
              </figure>
              <h3 className="text-primary-800 text-2xl">
                {t('simplifyMedicalBillingAndAdministrationWithTechnology')}
              </h3>
            </article>
          </SwiperSlide>
          <SwiperSlide>
            <article className="text-left card-hover">
              <figure className="relative rounded-xl overflow-hidden h-56 w-full mb-7 blog-card-image">
                <div className="w-full h-full">
                  <Image
                    loading="lazy"
                    fill={true}
                    objectFit="cover"
                    src="/images/comprehensive-guide-to-choosing-the-right-medical-software.webp"
                    alt=""
                  />
                </div>
              </figure>
              <h3 className="text-primary-800 text-2xl">
              {t('simplifyMedicalBillingAndAdministrationWithTechnology')}
              </h3>
            </article>
          </SwiperSlide>
          <div className="absolute z-10 -right-16 top-0 bg-slider-gradient h-full w-52 max-md:hidden"></div>
          <button aria-label="Previous slide" className="prev-slide absolute top-17.5 z-20 [&.swiper-button-disabled]:opacity-0 left-5 max-md:hidden">
            <div className="relative">
              <div className="bg-primary-500 rounded-full w-14 h-14 drop-shadow-[0px_14px_50px_#BE57DD]"></div>
              <AngleRightBold className="h-5.5 absolute left-2/4 top-2/4 -translate-1/2 rotate-180" />
            </div>
          </button>
          <button aria-label="Next slide" className="next-slide absolute top-17.5 z-20 [&.swiper-button-disabled]:opacity-0 right-5 max-md:hidden">
            <div className="relative">
              <div className="bg-primary-500 rounded-full w-14 h-14 drop-shadow-[0px_14px_50px_#BE57DD]"></div>
              <AngleRightBold className="h-5.5 absolute left-2/4 top-2/4 -translate-1/2" />
            </div>
          </button>
        </Swiper>
      </div>
    </section>
  );
}
